import React, { useEffect } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import { useSuperState } from "helpers/hooks";
import { useApp } from 'common/AppContext';
import GmetrixStackedArea from "common/components/recharts/area/GMetrixStackedArea";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";

const UsageGraph = props => {

    const [state, setState] = useSuperState({
        loading: false,
        error: null,
        graph_data: null,
        graph_series: []
    });

    const { appstream_fleet_name } = props;
    const app = useApp();
    const { fleets = [] } = app;
    const { formatDate } = useIntl();
    const { api } = app;

    const start_date = props?.start_date ? moment(props?.start_date).toISOString() : moment().subtract(30, 'days').toISOString();
    const end_date = props?.end_date ? moment(props?.end_date).toISOString() : moment().toISOString();

    const refreshGraph = async () => {

        try {

            setState({ loading: true });

            let { results, series } = await api.get(`/api/appstream/daily/usage`, { appstream_fleet_name, start_date, end_date });

            results = Array.isArray(results) ? results : [];
            series = Array.isArray(series) ? series : [];

            if (series.length === 0 && results.length > 0) {
                series = Object.keys(results[0]).filter(key => key !== 'date' && key !== 'name');
            }

            let graph_data = [];

            for (let i = 0; i < results.length; i++) {

                let result = results[i] || {};
                let graph_point = {
                    name: formatDate(moment(result.date || moment(start_date).add(i, 'days')).toDate(), {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                };

                for (let j = 0; j < series.length; j++) {
                    let series_key = series[j];
                    graph_point[series_key] = result[series_key] || 0;
                }

                graph_data.push(graph_point);

            }

            setState({
                graph_data,
                graph_series: series,
                loading: false
            });

        } catch ({ err }) {

            setState({
                loading: false,
                error: err
            });

        }

    };

    useEffect(() => {
        refreshGraph();
    }, [appstream_fleet_name, start_date, end_date]);

    const { loading, error, graph_data, graph_series } = state;
    const series_labels = fleets.reduce((labels, fleet) => {
        if (fleet?.appstream_fleet_name && fleet?.name) {
            labels[fleet.appstream_fleet_name] = fleet.name;
        }
        return labels;
    }, {});

    const vibrant_series_colors = ['#A57FBB', '#C97C92', '#6EA06E', '#C6AE57', '#C58A45', '#6FA9A3', '#B37780'];

    return (
        <>
            <Card {...props}>
                <Card.Header>Usage</Card.Header>
                <Card.Body>
                    <Alert variant="danger" show={error} onClose={() => setState({ error: null })} dismissible>
                        {error}
                    </Alert>
                    {loading &&
                        <div className="text-center">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    }
                    {!loading && <>
                        {graph_data && graph_data.length > 0 ?
                            (
                                <GmetrixStackedArea
                                    x_data_key="name"
                                    y_data_keys={graph_series}
                                    series_labels={series_labels}
                                    series_colors={vibrant_series_colors}
                                    custom_tooltip_label_1="Sessions started"
                                    dep_label="Sessions"
                                    legend={graph_series.length > 1}
                                    data={graph_data}
                                    height={475}
                                />
                            )
                            :
                            (
                                <div className="d-flex align-items-center justify-content-center text-muted h-100">
                                    <FormattedMessage
                                        id="label.no_usage_data_found"
                                        defaultMessage="No usage data found"
                                    />
                                </div>
                            )
                        }
                    </>}
                </Card.Body>
            </Card>
        </>
    );

};

export default UsageGraph;
